import { z } from "zod";

const translationPath = "page.4uDonation.donors.form.errors.";

const autoCompleteObjectSchema = z
  .any()
  .refine((val) => val, { message: "required" })
  .transform((val) => (val && typeof val === "object" ? val.id : val));

const optionalAutoCompleteObjectSchema = z
  .any()
  .optional()
  .transform((val) => (val && typeof val === "object" ? val.id : val));

const optionalNullableString = z.preprocess(
  (val) => (val === null ? undefined : val),
  z.string().optional()
) as z.ZodType<string | undefined, z.ZodTypeDef>;

const coreAddressObjectSchema = z.object({
  city: autoCompleteObjectSchema.refine((val) => val, {
    message: `${translationPath}cityId`,
  }),
  addressType: autoCompleteObjectSchema.refine((val) => val, {
    message: `${translationPath}addressTypeId`,
  }),
  addressDescription: z
    .string()
    .min(1, { message: `${translationPath}addressDescription` }),
  complement: z.string().optional(),
  neighborhood: z.any().optional(),
  neighborhoodName: z.string().optional(),
  number: z.string().min(1, { message: `${translationPath}number` }),
  zipCode: z.string().min(1, { message: `${translationPath}zipCode` }),
  otherNeighborhood: z.boolean().optional(),
  provincy: optionalAutoCompleteObjectSchema,
});

const addressSchema = coreAddressObjectSchema
  .refine(
    (data) =>
      !data.otherNeighborhood ||
      (data.neighborhoodName && data.neighborhoodName.length > 0),
    {
      message: `${translationPath}neighborhoodName`,
      path: ["neighborhoodName"],
    }
  )
  .refine((data) => data.otherNeighborhood || data.neighborhood, {
    message: `${translationPath}neighborhoodRequired`,
    path: ["neighborhood"],
  });

const contactsSchema = z.object({
  id: z.string().optional(),
  donorContactTypeId: z.string().min(1, { message: `${translationPath}id` }),
  value: z.string().min(1, { message: `${translationPath}value` }),
});

const genericsSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().nullish(),
  value: z.string().nullish(),
  description: z.string().nullish(),
});

const individualSchema = z.object({
  id: z.string(),
  individualName: z.string(),
  document: z.string().optional(),
  birthDate: z.string().nullish(),
});

const legalEntitySchema = z.object({
  id: z.string(),
  corporateName: z.string(),
  document: z.string().optional(),
});

const ddiSchema = z.object({
  code: z.string().nullish(),
  ddiCode: z.string().nullish(),
  id: z.string().nullish(),
  name: z.string().nullish(),
  phoneMask: z.string().nullish(),
});

const contactsObjectSchema = z.object({
  value: z.string().min(1, { message: `${translationPath}value` }),
  id: z.string().optional(),
  donorContactType: genericsSchema.nullish(),
  ddiCode: ddiSchema.nullish(),
});

export const donorsSchema = z
  .object({
    individual: individualSchema.optional().nullable(),
    individualId: z.string().optional(),
    legalEntity: legalEntitySchema.optional().nullable(),
    legalEntityId: optionalNullableString,
    donorType: genericsSchema.optional().nullable(),
    donorTypeId: z.string().optional(),
    gender: genericsSchema.nullish(),
    genderId: z.string().optional(),
    integrationId: optionalNullableString,
    attendant: genericsSchema.nullish(),
    attendantId: z.string().nullish(),
    attendantSecondary: genericsSchema.nullish(),
    attendantSecondaryId: z.string().optional(),
    userIntegrationType: genericsSchema.nullish(),
    userIntegrationTypeId: z.string().optional(),
    name: z.string().min(1, { message: `${translationPath}name` }),
    document: z.string().min(1, { message: `${translationPath}document` }),
    birthDateField: z.date().nullish(),
    birthDate: z.string().optional(),
    addressess: z.array(addressSchema).optional(),
    contactsObject: z.array(contactsObjectSchema).optional(),
    contacts: z.array(contactsSchema).optional(),
  })
  .superRefine((val, ctx) => {
    if (!val.userIntegrationType && (val.attendant || val.attendantSecondary)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["userIntegrationType"],
        message: translationPath + "user_integration_type",
      });
    }
  });

export type TDonorsForm = z.infer<typeof donorsSchema>;
